import { useState } from 'react';
import { Check, Monitor, Moon, Palette, RotateCcw, Sun } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { appearanceChanged, appearanceReset } from '@/features/appearance/appearanceSlice';
import { themeChanged, type ThemePreference } from '@/features/tenant/tenantSlice';
import { isHex, normaliseHex } from '@/lib/colour';
import {
  brandRamp,
  chartColours,
  checkAppearance,
  isDefault,
  THEME_PRESETS,
  type Appearance,
} from '@/lib/theme';
import { Alert, Button, Card, CardBody, CardHeader, Field } from '@/components/ui';

const MODES: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Match device', icon: Monitor },
];

/**
 * Choosing what the system looks like.
 *
 * The change is applied as it is made rather than on a save button: the whole
 * screen is the preview, and a colour is judged against the screen it will be
 * used on, not against a swatch. Nothing here is sent to the server — the
 * choice belongs to this device, so the till at the counter and the laptop in
 * the office can differ.
 */
export function AppearanceCard() {
  const dispatch = useAppDispatch();
  const appearance = useAppSelector((state) => state.appearance);
  const mode = useAppSelector((state) => state.tenant.theme);
  const [brandText, setBrandText] = useState(appearance.brand);
  const [accentText, setAccentText] = useState(appearance.accent);

  const warnings = checkAppearance(appearance);
  const ramp = brandRamp(appearance.brand);
  const charts = chartColours(appearance, 'light');

  const update = (changes: Partial<Appearance>): void => {
    dispatch(appearanceChanged({ ...appearance, ...changes }));
  };

  const reset = (): void => {
    dispatch(appearanceReset());
    setBrandText('#3b6ef5');
    setAccentText('#0f9d76');
  };

  return (
    <Card>
      <CardHeader
        title="Colours"
        description="How this system looks on this device. Saved here, not on the server, so it follows the screen rather than the account."
        action={
          !isDefault(appearance) && (
            <Button variant="ghost" icon={RotateCcw} onClick={reset}>
              Reset
            </Button>
          )
        }
      />

      <CardBody className="space-y-6">
        <Field label="Light or dark">
          <div className="flex flex-wrap gap-2">
            {MODES.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={mode === option.value}
                onClick={() => dispatch(themeChanged(option.value))}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                  mode === option.value
                    ? 'border-brand-600 bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-100'
                    : 'border-subtle hover:bg-sunken'
                }`}
              >
                <option.icon className="size-4" />
                {option.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Start from a theme" hint="Then change anything you like below.">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {THEME_PRESETS.map((preset) => {
              const presetRamp = brandRamp(preset.appearance.brand);
              const chosen =
                normaliseHex(appearance.brand) === normaliseHex(preset.appearance.brand) &&
                normaliseHex(appearance.accent) === normaliseHex(preset.appearance.accent);

              return (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => {
                    dispatch(appearanceChanged(preset.appearance));
                    setBrandText(preset.appearance.brand);
                    setAccentText(preset.appearance.accent);
                  }}
                  className={`flex items-center gap-3 rounded-xl border p-3 text-left ${
                    chosen ? 'border-brand-600' : 'border-subtle hover:bg-sunken'
                  }`}
                >
                  <span className="flex shrink-0 gap-1">
                    {[600, 400, 200].map((step) => (
                      <span
                        key={step}
                        className="size-5 rounded-full"
                        style={{ background: presetRamp[step] }}
                      />
                    ))}
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1 text-sm font-medium">
                      {preset.name}
                      {chosen && <Check className="size-3.5" />}
                    </span>
                    <span className="block truncate text-xs text-muted">{preset.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <ColourField
            label="Brand colour"
            hint="Buttons, links and anything that says “this one”."
            value={appearance.brand}
            text={brandText}
            onText={setBrandText}
            onChange={(brand) => update({ brand })}
          />
          <ColourField
            label="Second colour"
            hint="The second line on a chart, beside the brand colour."
            value={appearance.accent}
            text={accentText}
            onText={setAccentText}
            onChange={(accent) => update({ accent })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tint of the greys" hint="How far the background and text lean towards a colour.">
            <input
              type="range"
              min={0}
              max={20}
              value={Math.round(appearance.neutralChroma * 1000)}
              onChange={(event) => update({ neutralChroma: Number(event.target.value) / 1000 })}
              className="w-full accent-brand-600"
              aria-label="Tint strength"
            />
            <p className="mt-1 text-xs text-muted">
              {appearance.neutralChroma === 0 ? 'Neutral grey' : 'Tinted'}
            </p>
          </Field>

          <Field label="Which way they lean" hint="The hue the greys are tinted towards.">
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={359}
                value={appearance.neutralHue}
                onChange={(event) => update({ neutralHue: Number(event.target.value) })}
                disabled={appearance.neutralChroma === 0}
                className="w-full accent-brand-600 disabled:opacity-40"
                aria-label="Tint hue"
              />
              {/* The tint is too faint to read off a swatch, so the surfaces
                  themselves are the sample: this is the page, at this hue. */}
              <span className="flex shrink-0 overflow-hidden rounded-md border border-subtle">
                {['--surface', '--surface-sunken', '--surface-raised'].map((token) => (
                  <span
                    key={token}
                    className="size-6"
                    style={{ background: `var(${token})` }}
                  />
                ))}
              </span>
            </div>
          </Field>
        </div>

        {warnings.length > 0 && (
          <div className="space-y-2">
            {warnings.map((warning) => (
              <Alert key={warning.field} tone="warning">
                {warning.message}
              </Alert>
            ))}
          </div>
        )}

        <Field label="How it will look">
          <div className="rounded-xl border border-subtle p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-white"
                style={{ background: ramp[600] }}
              >
                Record a sale
              </span>
              <span
                className="rounded-lg border px-3 py-1.5 text-sm"
                style={{ borderColor: ramp[600], color: ramp[700] }}
              >
                Cancel
              </span>
              <span
                className="rounded-full px-2 py-0.5 text-xs font-medium"
                style={{ background: ramp[100], color: ramp[800] }}
              >
                Paid
              </span>
            </div>

            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="h-0.5 w-6 rounded" style={{ background: charts.chart1 }} />
                Revenue
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-0.5 w-6 rounded" style={{ background: charts.chart2 }} />
                Profit
              </span>
            </div>

            <div className="mt-3 flex gap-1">
              {Object.entries(ramp).map(([step, hex]) => (
                <span
                  key={step}
                  title={`${step} · ${hex}`}
                  className="h-6 flex-1 first:rounded-l last:rounded-r"
                  style={{ background: hex }}
                />
              ))}
            </div>
          </div>
        </Field>

        <p className="flex items-start gap-2 text-xs text-muted">
          <Palette className="mt-0.5 size-3.5 shrink-0" />
          These colours are kept in this browser. Someone signing in on another device sees the
          standard theme until they choose their own.
        </p>
      </CardBody>
    </Card>
  );
}

function ColourField({
  label,
  hint,
  value,
  text,
  onText,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  text: string;
  onText: (value: string) => void;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          aria-label={label}
          onChange={(event) => {
            onText(event.target.value);
            onChange(event.target.value);
          }}
          className="size-10 shrink-0 cursor-pointer rounded-lg border border-subtle bg-transparent p-1"
        />
        <input
          type="text"
          value={text}
          spellCheck={false}
          aria-label={`${label} hex code`}
          onChange={(event) => {
            onText(event.target.value);
            // Typing a hex code is applied the moment it becomes a colour, so
            // a half-typed value never repaints the screen.
            if (isHex(event.target.value)) onChange(normaliseHex(event.target.value));
          }}
          className="w-full rounded-lg border border-subtle bg-transparent px-3 py-2 font-mono text-sm"
        />
      </div>
    </Field>
  );
}
